// Physics step — pure(ish) function. Mutates `state` (the car) and
// `der` (telemetry) in place; reads `spec`, `inputs`, and `dt`. Same
// inputs always produce the same output (provided surface + spec are
// the same). This is what enables replays, debugging, and headless
// full-grid simulations.
//
// Two-axle yaw model with friction ellipse:
//   1. Smooth steering toward driver intent (speed-sensitive authority).
//   2. Decompose velocity into car-local frame (forward / lateral).
//   3. Longitudinal force = engine (P/v capped by grip) + brake + drag
//      + rolling + surface drag.
//   4. Per-axle lateral forces from slip angles, capped by grip.
//   5. Yaw torque from front/rear lateral forces, integrated against
//      yaw inertia with damping.
//   6. Apply combined acceleration in world frame; integrate heading
//      and position.

import { SURFACE_PROPS } from '../../shared/surfaces.js';
import { clamp } from '../../shared/geometry.js';

export function physicsStep(state, ins, spec, der, dt) {
  // 1. Speed-sensitive steering authority.
  //    Below steerSpeedRef: full lock. Above: max steering shrinks as
  //    ref/v, floored by minSteerMul. Keyboard equivalent of "you
  //    wouldn't yank the wheel at 200 km/h".
  const speed             = Math.hypot(state.vel.x, state.vel.y);
  const speedSteerMul     = Math.max(spec.minSteerMul, spec.steerSpeedRef / Math.max(speed, spec.steerSpeedRef));
  const effectiveMaxSteer = spec.maxSteeringAngle * speedSteerMul;
  const targetSteer       = ins.steerTarget * effectiveMaxSteer;
  const maxDelta          = spec.steeringRate * dt;
  state.steer            += clamp(targetSteer - state.steer, -maxDelta, maxDelta);
  state.steer             = clamp(state.steer, -effectiveMaxSteer, effectiveMaxSteer);
  der.steerAuth           = speedSteerMul;

  // 2. Local-frame velocity
  const cos = Math.cos(state.heading);
  const sin = Math.sin(state.heading);
  const fwd =  state.vel.x * cos + state.vel.y * sin;
  const lat = -state.vel.x * sin + state.vel.y * cos;

  // 3. Longitudinal forces
  const surfProps = SURFACE_PROPS[der.surface] || SURFACE_PROPS.grass;
  const gripMul   = surfProps.gripMul;
  const maxLongF  = spec.tractionGrip * spec.mass * 9.81 * gripMul;
  const engineRaw = (spec.enginePower / Math.max(Math.abs(fwd), 4)) * ins.throttle;
  const engineF   = Math.min(engineRaw, maxLongF);                   // capped — assume perfect TC
  const brakeF    = -spec.brakeForce * ins.brake * Math.sign(fwd || 0.001);
  const dragF     = -spec.dragCoeff * fwd * Math.abs(fwd);
  const rollF     = -spec.rollingResistance * fwd;
  const surfDragF = -surfProps.drag * fwd;                           // gravel digs in
  let   longF     = engineF + brakeF + dragF + rollF + surfDragF;

  // 4. Per-axle lateral forces. CoM assumed mid-wheelbase. Front-axle
  //    force creates the yaw torque that rotates the car.
  const aHalf   = spec.wheelbase * 0.5;
  const latVelF = lat + state.angVel * aHalf;
  const latVelR = lat - state.angVel * aHalf;
  const slipF   = Math.atan2(latVelF, Math.max(Math.abs(fwd), 1)) - state.steer;
  const slipR   = Math.atan2(latVelR, Math.max(Math.abs(fwd), 1));

  const totalLatGrip = spec.grip * spec.mass * 9.81 * gripMul;
  const maxLatF_F    = totalLatGrip * spec.frontGripBias;
  const maxLatF_R    = totalLatGrip * (1 - spec.frontGripBias);

  // No tire force when nearly stationary (atan2 noise).
  const speedFactor = Math.min(1, Math.abs(fwd) / 5);

  const latF_F = clamp(-spec.corneringStiffness * slipF * maxLatF_F * speedFactor, -maxLatF_F, maxLatF_F);
  const latF_R = clamp(-spec.corneringStiffness * slipR * maxLatF_R * speedFactor, -maxLatF_R, maxLatF_R);

  // Front tire is rotated by the steering angle, so its side force has
  // both a car-lateral component AND a small backward component
  // (corner scrub — bleeds speed in corners, realistic).
  const cosS         = Math.cos(state.steer);
  const sinS         = Math.sin(state.steer);
  const localLatF    =  latF_F * cosS + latF_R;
  const localScrub   = -latF_F * sinS;
  longF             += localScrub;

  const longA = longF / spec.mass;
  const latA  = localLatF / spec.mass;

  // 5. Yaw dynamics — torque integrates against inertia, with damping.
  const yawTorque = latF_F * aHalf * cosS - latF_R * aHalf;
  state.angVel   += (yawTorque / spec.yawInertia) * dt;
  state.angVel   *= Math.exp(-spec.yawDamping * dt);

  // 6. Apply combined acceleration in world frame; integrate.
  const ax = longA * cos - latA * sin;
  const ay = longA * sin + latA * cos;
  state.vel.x += ax * dt;
  state.vel.y += ay * dt;

  state.heading += state.angVel * dt;
  state.pos.x   += state.vel.x  * dt;
  state.pos.y   += state.vel.y  * dt;

  // Telemetry
  der.fwdSpeed  = fwd;
  der.latSpeed  = lat;
  der.lateralG  = latA / 9.81;
  der.longG     = longA / 9.81;
  der.slipAngle = computeSlipAngle(state);
  der.slipF     = slipF;
  der.slipR     = slipR;
}

export function computeSlipAngle(state) {
  const speed = Math.hypot(state.vel.x, state.vel.y);
  if (speed < 1) return 0;
  const velAngle = Math.atan2(state.vel.y, state.vel.x);
  let diff = velAngle - state.heading;
  while (diff >  Math.PI) diff -= 2 * Math.PI;
  while (diff < -Math.PI) diff += 2 * Math.PI;
  return diff;
}
